import { useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, CheckCircle2, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";

const TEMPLATE = `entry_date,vehicle_number,party_name,transporter_name,material_name,material_weight,material_purchase_rate,material_bill_rate,material_gst_percent,po_number,po_date,unloading_date,transport_weight,transport_purchase_rate,transport_bill_rate,transport_gst_percent,notes
2026-02-15,WB37H-1907,ABC INFRA,ROYAL TRANSPORTS,SAND,25,280,420,5,PO-2026-001,2026-02-10,2026-02-15,25,60,90,0,
2026-02-16,WB37H-2200,ABC INFRA,ROYAL TRANSPORTS,10 MM STONE CHIPS,28,350,500,5,PO-2026-001,2026-02-10,2026-02-16,28,55,85,0,`;

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line) => {
    // simple CSV — no quoted comma support, sufficient for our template
    const cells = line.split(",").map((c) => c.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cells[i] ?? ""; });
    // Cast numeric fields
    ["material_weight", "material_purchase_rate", "material_bill_rate", "material_gst_percent",
     "transport_weight", "transport_purchase_rate", "transport_bill_rate", "transport_gst_percent"].forEach((k) => {
      obj[k] = parseFloat(obj[k] || 0) || 0;
    });
    return obj;
  });
}

export default function BulkImport() {
  const [csv, setCsv] = useState("");
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const parse = () => {
    try {
      const r = parseCSV(csv);
      if (r.length === 0) return toast.error("No rows found");
      setRows(r);
      setResult(null);
      toast.success(`Parsed ${r.length} row${r.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error("CSV parse failed: " + e.message); }
  };

  const importNow = async () => {
    if (rows.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/trucks/bulk-import", rows);
      setResult(data);
      toast.success(`${data.inserted} of ${data.total} rows imported`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "tulsi-bulk-truck-import-template.csv"; a.click();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-5xl mx-auto" data-testid="bulk-import-page">
      <PageHeader
        title="Bulk Truck Import"
        subtitle="Paste CSV rows or upload a CSV file. Party / Transporter / Material are matched by NAME (case-insensitive). Create them in Masters first if missing."
        action={
          <Button variant="outline" onClick={downloadTemplate} className="rounded-sm" data-testid="download-template-btn">
            <Download className="h-4 w-4 mr-1.5" /> CSV Template
          </Button>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none p-5 mb-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-3">1. Paste CSV</div>
        <textarea
          rows={10}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          data-testid="csv-textarea"
          placeholder={TEMPLATE}
          className="w-full text-xs font-mono p-3 border border-brand-line rounded-sm bg-white"
        />
        <div className="mt-3 flex gap-2 justify-end">
          <input type="file" accept=".csv" hidden id="csv-file" onChange={(e) => {
            const f = e.target.files?.[0]; if (!f) return;
            const r = new FileReader(); r.onload = () => setCsv(r.result); r.readAsText(f);
          }} />
          <label htmlFor="csv-file" className="cursor-pointer">
            <Button variant="outline" asChild className="rounded-sm" data-testid="upload-csv-btn">
              <span><Upload className="h-4 w-4 mr-1.5" /> Upload CSV</span>
            </Button>
          </label>
          <Button onClick={parse} data-testid="parse-btn" className="rounded-sm bg-brand-forest text-white">Parse & Preview</Button>
        </div>
      </Card>

      {rows.length > 0 && (
        <Card className="rounded-sm border-brand-line shadow-none mb-5 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-brand-line">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">2. Preview</div>
              <div className="font-serif text-xl text-brand-ink">{rows.length} row{rows.length > 1 ? "s" : ""} ready to import</div>
            </div>
            <Button onClick={importNow} disabled={busy} data-testid="import-now-btn" className="rounded-sm bg-brand-forest text-white">
              {busy ? "Importing…" : "Import Now"}
            </Button>
          </div>
          <div className="overflow-x-auto max-h-[40vh]">
            <table className="w-full text-xs">
              <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.1em] text-brand-muted sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Vehicle</th>
                  <th className="text-left px-3 py-2">Party</th>
                  <th className="text-left px-3 py-2">Transporter</th>
                  <th className="text-left px-3 py-2">Material</th>
                  <th className="text-right px-3 py-2">Mat Wgt</th>
                  <th className="text-right px-3 py-2">Mat Bill ₹</th>
                  <th className="text-right px-3 py-2">Trn ₹</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-brand-line">
                    <td className="px-3 py-1.5 tabular-nums">{i + 1}</td>
                    <td className="px-3 py-1.5">{r.entry_date}</td>
                    <td className="px-3 py-1.5 font-medium">{r.vehicle_number}</td>
                    <td className="px-3 py-1.5">{r.party_name}</td>
                    <td className="px-3 py-1.5">{r.transporter_name}</td>
                    <td className="px-3 py-1.5">{r.material_name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.material_weight}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.material_bill_rate}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.transport_bill_rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {result && (
        <Card className="rounded-sm border-brand-line shadow-none">
          <div className="px-5 py-4 border-b border-brand-line flex items-center gap-3">
            <Badge className={result.inserted === result.total ? "bg-brand-forest text-white" : "bg-brand-sand/30 text-brand-terracotta"}>
              {result.inserted} / {result.total} imported
            </Badge>
            <div className="text-xs text-brand-muted">{result.total - result.inserted} failed</div>
          </div>
          <div className="max-h-[40vh] overflow-y-auto">
            <table className="w-full text-xs">
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className="border-t border-brand-line">
                    <td className="px-3 py-1.5 w-12 tabular-nums">{r.row}</td>
                    <td className="px-3 py-1.5">
                      {r.ok ? (
                        <span className="inline-flex items-center gap-1 text-brand-forest">
                          <CheckCircle2 className="h-3.5 w-3.5" /> {r.vehicle}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-brand-terracotta">
                          <AlertCircle className="h-3.5 w-3.5" /> {r.error}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
